import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Boxes, FolderTree, Lock, Plus } from 'lucide-react';
import {
  STOCK_CATEGORIES,
  formatWithUnit,
  type StockCategory,
  type StockItem,
} from '@tailoring-crm/shared';
import { EmptyState } from '../components/feedback/empty-state.js';
import { LoadError } from '../components/feedback/load-error.js';
import { PageHeader } from '../components/layout/page-header.js';
import { QuotaLine } from '../components/billing/resource-meter.js';
import { Badge, Button, Input, ListSkeleton, cn } from '@tailoring-crm/ui';
import { useAuth } from '../lib/auth/auth-context.js';
import { formatCurrency } from '../lib/format.js';
import { useQuota } from '../lib/billing/use-billing.js';
import { useStockGroups, useStockList } from '../lib/inventory/use-stock.js';

/**
 * What the shop holds.
 *
 * The list leads with what is LOW rather than with what is valuable, because the question
 * somebody opens this screen with is almost always "do we have enough of that for Saturday?".
 * The valuation is at the bottom, where an owner doing their accounts will find it and a cutter
 * looking for teal silk will not have to read past it.
 *
 * Every quantity on this screen is derived from the movement ledger. There is no field to type a
 * quantity into anywhere in this module — see the API — so a number here can always be traced to
 * the rows that produced it.
 */
export function StockPage(): ReactElement {
  const { t } = useTranslation();
  const { role } = useAuth();
  const [q, setQ] = useState('');
  const [category, setCategory] = useState<StockCategory | null>(null);
  const [lowOnly, setLowOnly] = useState(false);

  /**
   * The group filter lives in the URL rather than in state, because it is arrived at from
   * somewhere else: the groups screen's "open the items" is a link to `/stock?groupId=…`, and a
   * filtered shelf is a thing somebody sends to somebody else. The other three filters are
   * typed here and stay here.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const groupId = searchParams.get('groupId');

  const setGroup = (next: string | null): void => {
    const params = new URLSearchParams(searchParams);
    if (next) params.set('groupId', next);
    else params.delete('groupId');
    setSearchParams(params, { replace: true });
  };

  // Every line of this module carries a cost price, so a karigar is refused all of it.
  const allowed = role !== 'tailor';
  const list = useStockList(
    {
      ...(q ? { q } : {}),
      ...(category ? { category } : {}),
      ...(groupId ? { groupId } : {}),
      lowOnly,
    },
    { enabled: allowed },
  );
  const groups = useStockGroups({}, { enabled: allowed });
  // Above the access refusal below, because hooks may not sit after a return.
  const stockQuota = useQuota('stock_items');

  if (!allowed) {
    return (
      <>
        <PageHeader title={t('stock.title')} />
        <EmptyState
          icon={Lock}
          title={t('stock.noAccessTitle')}
          description={t('stock.noAccessHint')}
        />
      </>
    );
  }

  const data = list.data;

  return (
    <>
      <PageHeader
        title={t('stock.title')}
        description={t('stock.description')}
        action={
          <span className="flex flex-wrap items-center gap-2">
            {/* The headings are reached from the shelf, because the shelf is where somebody
                already is when they decide it needs arranging. */}
            <Button asChild variant="outline">
              <Link to="/stock/groups">
                <FolderTree aria-hidden="true" />
                {t('stockGroups.title')}
              </Link>
            </Button>
            <Button asChild>
              <Link to="/stock/new">
                <Plus aria-hidden="true" />
                {t('stock.add')}
              </Link>
            </Button>
          </span>
        }
      />

      <div className="flex flex-col gap-4">
        {/* The shelf's allowance, and only once it is close. See `QuotaLine`. */}
        <QuotaLine usage={stockQuota} />

        <Input
          type="search"
          value={q}
          placeholder={t('stock.searchPlaceholder')}
          aria-label={t('stock.searchPlaceholder')}
          onChange={(event) => setQ(event.target.value)}
        />

        {/* The headings, when the shop keeps any. One row of chips that narrows to a group and
            clears back to the whole shelf — never a mode the list gets stuck in. */}
        {groups.data && groups.data.items.length > 0 ? (
          <div
            className="flex flex-wrap gap-1.5 overflow-x-auto"
            role="group"
            aria-label={t('stockGroups.filterLabel')}
          >
            <Chip active={groupId === null} onClick={() => setGroup(null)}>
              {t('stockGroups.allGroups')}
            </Chip>
            {groups.data.items.map((group) => (
              <Chip
                key={group.id}
                active={groupId === group.id}
                onClick={() => setGroup(groupId === group.id ? null : group.id)}
              >
                {group.name}
              </Chip>
            ))}
          </div>
        ) : null}

        <div className="flex flex-wrap gap-1.5 overflow-x-auto">
          <Chip active={lowOnly} onClick={() => setLowOnly(!lowOnly)}>
            {t('stock.lowOnly')}
            {data && data.lowStockCount > 0 ? ` (${String(data.lowStockCount)})` : ''}
          </Chip>
          {STOCK_CATEGORIES.map((one) => (
            <Chip
              key={one}
              active={category === one}
              onClick={() => setCategory(category === one ? null : one)}
            >
              {t(`stock.category.${one}`)}
            </Chip>
          ))}
        </div>

        {list.isPending ? <ListSkeleton rows={5} /> : null}

        {list.isError ? <LoadError onRetry={() => void list.refetch()} /> : null}

        {data && data.items.length === 0 ? (
          <EmptyState
            icon={Boxes}
            title={t('stock.emptyTitle')}
            description={t('stock.emptyDescription')}
            action={
              <Button asChild>
                <Link to="/stock/new">{t('stock.add')}</Link>
              </Button>
            }
          />
        ) : null}

        {data && data.items.length > 0 ? (
          <>
            <ul className="flex flex-col gap-1.5">
              {data.items.map((item) => (
                <li key={item.id}>
                  <StockRow item={item} />
                </li>
              ))}
            </ul>

            <p className="text-sm text-muted-foreground" role="status">
              {t('stock.valuation', { amount: formatCurrency(data.totalValuePaise) })}
            </p>
          </>
        ) : null}
      </div>
    </>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): ReactElement {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'shrink-0 rounded-full border px-3 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active
          ? 'border-transparent bg-primary text-primary-foreground'
          : 'border-border hover:bg-accent',
      )}
    >
      {children}
    </button>
  );
}

function StockRow({ item }: { item: StockItem }): ReactElement {
  const { t } = useTranslation();

  return (
    <Link
      to={`/stock/${item.id}`}
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3 text-sm hover:bg-accent',
        item.lowStock ? 'border-destructive/40 bg-destructive/5' : 'border-border',
      )}
    >
      <span className="min-w-0">
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{item.name}</span>
          {item.lowStock ? (
            <Badge variant="destructive">
              <AlertTriangle aria-hidden="true" className="size-3" />
              {t('stock.low')}
            </Badge>
          ) : null}
        </span>
        <span className="block text-muted-foreground">
          {/* Denormalised onto the item, so the heading is here without a second fetch. */}
          {item.groupName ? `${item.groupName} · ` : ''}
          {t(`stock.category.${item.category}`)}
          {item.location ? ` · ${item.location}` : ''} ·{' '}
          {t('stock.colourCount', { count: item.variants.filter((one) => !one.archived).length })}
        </span>
      </span>
      <span className="text-right">
        <span className="block font-semibold tabular-nums">
          {formatWithUnit(item.quantity, item.unit)}
        </span>
        <span className="block text-xs text-muted-foreground tabular-nums">
          {formatCurrency(item.valuePaise)}
        </span>
      </span>
    </Link>
  );
}

export default StockPage;
